import { DotsCheckbox } from "dots-ui/dotsCheckbox";
import { FormikProps } from "formik";
import moment from "moment";
import { useContext, useMemo } from "react";
import {
  ItemViewModel,
  SubmitShowsReport,
  SubmitShowsReportRow,
} from "../../../../../generated/data-contracts";
import {
  AvisEditableList,
  IAvisEditableListFieldConfig,
  IRowState,
} from "../../../../shared/components/editableList";
import {
  AvisDescriptiveText,
  AvisSection,
} from "../../../../shared/components/form";
import {
  AvisCalendar,
  AvisDropdown,
  AvisInputDecimal,
  AvisInputInt,
} from "../../../../shared/components/inputs";
import { AvisContext } from "../../../../shared/context/AvisContext";

interface IReportShowsFormProps {
  bag: FormikProps<SubmitShowsReport>;
  bagField: keyof SubmitShowsReport;
  periodFrom: Date | undefined;
  periodTo: Date | undefined;
  disabled: boolean;
  loading: boolean;
  allowBlankReport: boolean | undefined;
  availableCreations: ItemViewModel[] | undefined;
}
export function ReportShowsForm(props: Readonly<IReportShowsFormProps>) {
  const {
    bag,
    bagField,
    periodFrom,
    periodTo,
    allowBlankReport,
    disabled,
    loading,
    availableCreations,
  } = props;
  const { res } = useContext(AvisContext);
  const hideClear = true;
  const rowInitValue = useMemo(() => {
    return {
      licenseClientEventId: undefined,
      eventDate: "",
      creationId: 0,
      soldTickets: undefined,
      grossRevenue: undefined,
      totalInvitations: undefined,
      withEntryFee: false,
      totalVisitorsWithoutTicket: undefined,
    };
  }, []);

  const yearTo = periodFrom?.getFullYear();
  const yearFrom = periodTo?.getFullYear();
  const yearRangeStr = `${yearFrom}:${yearTo}`;
  const fieldCollection: IAvisEditableListFieldConfig<
    IRowState<SubmitShowsReportRow>
  >[] = [
    {
      name: "creationId",
      title: res("licensees_LicenseReports_type1_label_Creation"),
      required: true,
      minWidth: 150,
      width: 500,
      transformViewValue: (fieldValue) => {
        return (
          availableCreations?.filter((item) => item.id == fieldValue)?.[0]
            .name ?? ""
        );
      },
      renderField: (field, prefix, i, val, onChange) => {
        return (
          <AvisDropdown<ItemViewModel>
            name={`${prefix}.${i}.${field.name}`}
            value={val[field.name]}
            onChange={onChange}
            options={availableCreations}
            optionValue="id"
            optionLabel="name"
            disabled={disabled}
            required={field.required}
            hideClear
          />
        );
      },
    },
    {
      name: "eventDate",
      title: res("label_date"),
      required: true,
      minWidth: 100,
      width: 100,
      dataType: "Date",
      renderField: (field, prefix, i, val, onChange) => {
        return (
          <AvisCalendar
            name={`${prefix}.${i}.${field.name}`}
            value={val.eventDate ? moment(val.eventDate).toDate() : undefined}
            disabled={disabled}
            onChange={onChange}
            minDate={periodFrom}
            maxDate={periodTo}
            yearRange={yearRangeStr}
            defaultDateToShow={periodFrom}
            required={field.required}
            hideClear
          />
        );
      },
    },

    {
      name: "withEntryFee", //Used only for UI element
      title: res("licensees_LicenseReports_type2_label_WithEntranceFee"),
      width: 75,
      forceEditMode: true,
      renderField: (field, prefix, index, value, onChange) => {
        return (
          <DotsCheckbox
            name={`${prefix}.${index}.${field.name}`}
            className="license-report-event-checkbox-column"
            spacing="small"
            checked={value.withEntryFee}
            simpleInput
            onChange={onChange}
            disabled={
              disabled ||
              !value.rowState ||
              value.rowState === "View" ||
              value.rowState === "Delete"
            }
          />
        );
      },
    },
    {
      name: "soldTickets",
      title: res("licensees_LicenseReports_type2_label_SoldTickets"),
      shouldUpdateOnRender: true,
      minWidth: 100,
      width: 150,
      renderField: (field, prefix, index, value, onChange) => {
        return (
          <AvisInputInt
            name={`${prefix}.${index}.${field.name}`}
            value={value.withEntryFee ? value[field.name]?.toString() : ""}
            onChange={onChange}
            disabled={disabled || !value.withEntryFee}
            hideClear={hideClear}
          />
        );
      },
    },
    {
      name: "grossRevenue",
      title: res("licensees_LicenseReports_type2_label_GrossRevenue"),
      dataType: "Double",
      shouldUpdateOnRender: true,
      minWidth: 100,
      width: 150,
      renderField: (field, prefix, index, value, onChange) => {
        return (
          <AvisInputDecimal
            name={`${prefix}.${index}.${field.name}`}
            value={value.withEntryFee ? value[field.name]?.toString() : ""}
            onChange={onChange}
            disabled={disabled || !value.withEntryFee}
            hideClear={hideClear}
          />
        );
      },
    },
    {
      name: "totalInvitations",
      title: res("licensees_LicenseReports_type2_label_TotalInvitations"),
      shouldUpdateOnRender: true,
      minWidth: 100,
      width: 150,
      renderField: (field, prefix, index, value, onChange) => {
        return (
          <AvisInputInt
            name={`${prefix}.${index}.${field.name}`}
            value={value.withEntryFee ? value[field.name]?.toString() : ""}
            onChange={onChange}
            disabled={disabled || !value.withEntryFee}
            hideClear={hideClear}
          />
        );
      },
    },
    {
      name: "totalVisitorsWithoutTicket",
      title: res(
        "licensees_LicenseReports_type2_label_TotalVisitorsWithoutTicket",
      ),
      minWidth: 150,
      width: 200,
      shouldUpdateOnRender: true,
      renderField: (field, prefix, index, value, onChange) => {
        return (
          <AvisInputInt
            name={`${prefix}.${index}.${field.name}`}
            value={!value.withEntryFee ? value[field.name]?.toString() : ""}
            onChange={onChange}
            disabled={disabled || value.withEntryFee}
            hideClear={hideClear}
          />
        );
      },
    },
  ];

  return (
    <AvisSection title={res("licensees_LicenseReports_label_SectionDataShows")}>
      <AvisDescriptiveText textKey="portal_LIC_LicenseReportForm1" />
      {allowBlankReport && (
        <DotsCheckbox<SubmitShowsReport>
          bag={bag}
          bagField="eventNotOccuredPortal"
          checkboxLabel={res(
            "licensees_LicenseReports_shared_label_EventNotOccured",
          )}
          disabled={disabled}
        />
      )}
      {!bag.values.eventNotOccuredPortal && (
        <AvisEditableList<SubmitShowsReport, SubmitShowsReportRow>
          bag={bag}
          bagField={bagField}
          initValue={rowInitValue}
          confirmBeforeDelete={(data) => {
            if (JSON.stringify(data) === JSON.stringify(rowInitValue))
              return undefined;

            return {
              content: res("message_DeleteRow"),
              mustShow: true,
            };
          }}
          fields={fieldCollection}
          disabled={disabled}
          loading={loading}
        />
      )}
    </AvisSection>
  );
}
